import { useState } from "react";
import { Label } from "../label";
import { Button } from "../button";
import { Badge } from "../badge";
import { CheckIcon, ChevronsUpDownIcon, XIcon } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "../command";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";

import { HGCollection } from "@/common/types";

export interface CollectionChildrenComboboxProps {
  allCollections: HGCollection[];
  label?: string;
  placeholder?: string;
  instant?: boolean;
  value: string[];
  onChange(value: string[]): void;
}

export default function CollectionCombobox({
  allCollections,
  label,
  placeholder,
  instant,
  value,
  onChange,
}: CollectionChildrenComboboxProps) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const [selectedValues, setSelectedValues] = useState<string[]>(value);
  // `value` compared by content, not identity: callers pass a freshly
  // derived array on every render, so a reference check would resync (and
  // discard in-progress local edits) on every keystroke.
  const valueKey = JSON.stringify(value);
  const [selectedValuesForKey, setSelectedValuesForKey] = useState(valueKey);
  if (valueKey !== selectedValuesForKey) {
    setSelectedValuesForKey(valueKey);
    setSelectedValues(value);
  }

  const toggleSelection = (value: string) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    setSelectedValues(newValues);
    if (instant) {
      onChange(newValues);
    }
  };

  const removeSelection = (value: string) => {
    const newValues = selectedValues.filter((v) => v !== value);
    setSelectedValues(newValues);
    if (instant) {
      onChange(newValues);
    }
  };

  // Define maxShownItems before using visibleItems
  const maxShownItems = 2;
  const visibleItems = expanded
    ? selectedValues
    : selectedValues.slice(0, maxShownItems);
  const hiddenCount = selectedValues.length - visibleItems.length;

  const handleSave = () => {
    onChange(selectedValues);
  };

  const collectionsToRemove = value.filter(
    (colID) => !selectedValues.includes(colID),
  );

  const collectionsToAdd = selectedValues.filter(
    (collectionID) => !value.includes(collectionID),
  );

  return (
    <div className="flex flex-col gap-2">
      {label && <Label htmlFor="collectionsCombobox" className="text-sm font-medium">{label}</Label>}
      <div className="flex gap-2 items-center">
        <Popover open={open} onOpenChange={setOpen} modal={true}>
          <PopoverTrigger asChild>
            <Button
              id="collectionsCombobox"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="grow shrink h-auto min-h-8 justify-between hover:bg-transparent"
            >
              <div className="flex flex-wrap items-center gap-1 pr-2.5">
                {selectedValues.length > 0 ? (
                  <>
                    {visibleItems.map((val) => {
                      const collection = allCollections.find(
                        (c) => c.id.toString() === val,
                      );

                      return collection ? (
                        <Badge
                          key={val}
                          variant="outline"
                          className="rounded-sm"
                        >
                          {collection.title}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-4"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeSelection(val);
                            }}
                            asChild
                          >
                            <span>
                              <XIcon className="size-3" />
                            </span>
                          </Button>
                        </Badge>
                      ) : null;
                    })}
                    {hiddenCount > 0 || expanded ? (
                      <Badge
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpanded((prev) => !prev);
                        }}
                        className="rounded-sm"
                      >
                        {expanded ? "Show Less" : `+${hiddenCount} more`}
                      </Badge>
                    ) : null}
                  </>
                ) : (
                  <span className="text-muted-foreground">
                    {placeholder || "Select collection"}
                  </span>
                )}
              </div>
              <ChevronsUpDownIcon
                className="text-muted-foreground/80 shrink-0"
                aria-hidden="true"
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-(--radix-popper-anchor-width) p-0">
            <Command>
              {/* <CommandInput placeholder="Search collection..." /> */}
              <CommandList>
                <CommandEmpty>No collections found.</CommandEmpty>
                <CommandGroup>
                  {allCollections.map((collection) => (
                    <CommandItem
                      key={collection.id}
                      value={collection.id}
                      onSelect={() => toggleSelection(collection.id)}
                    >
                      <span className="truncate">{collection.title}</span>
                      {selectedValues.includes(collection.id) && (
                        <CheckIcon size={16} className="ml-auto" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {!instant &&
          (collectionsToRemove.length > 0 || collectionsToAdd.length > 0) && (
            <Button onClick={handleSave}>Save</Button>
          )}
      </div>
    </div>
  );
}
